import React, { useState, useEffect } from 'react';
import { generateClient } from 'aws-amplify/data';
import type { Schema } from '../amplify/data/resource';
import StatusMessage from '../components/StatusMessage';
import ConfirmDialog from '../components/ConfirmDialog';


interface Course {
  id: string;
  courseId?: string; // Admin-managed ID field
  title: string;
  punchline?: string;
  shortDescription?: string;
  description: string;
  price: string;
  tutorId?: string;
  tutor?: {
    id: string;
    name: string;
    photo?: string;
    bio?: string;
  };
  location?: string;
  urlSlug?: string;
  partnerId?: string;
  startDate?: string;
  endDate?: string;
  targetgroup?: string;
  isActive: boolean;
  environment: string;
  createdAt: string;
  updatedAt: string;
  partner?: {
    id: string;
    name: string;
  };
  sessions?: Array<{
    id: string;
    title: string;
    date: string;
    startTime: string;
    endTime: string;
    locationType: 'ONLINE' | 'ON_SITE';
    order?: number; // Session order/sequence
  }>;
}

type SortField = 'courseId' | 'title' | 'price' | 'location' | 'urlSlug' | 'startDate' | 'endDate' | 'isActive';
type SortDirection = 'asc' | 'desc';

interface AdminCoursesOverviewProps {
  onEditCourse?: (courseId: string) => void;
}

const AdminCoursesOverview: React.FC<AdminCoursesOverviewProps> = ({ onEditCourse }) => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'warning' | 'info'; text: string } | null>(null);
  const [confirmDialog, setConfirmDialog] = useState<{ isOpen: boolean; courseId: string; courseTitle: string }>({ isOpen: false, courseId: '', courseTitle: '' });
  const [sortField, setSortField] = useState<SortField>('title');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [showDeactivatedTable, setShowDeactivatedTable] = useState<boolean>(false);
  const [showDeletedTable, setShowDeletedTable] = useState<boolean>(false); // New: deleted items tab
  const [participantCounts, setParticipantCounts] = useState<Record<string, { open: number; invoiced: number; paid: number }>>({});

  useEffect(() => {
    fetchCourses();
  }, []);

  const fetchCourses = async () => {
    setLoading(true);
    
    try {
      // Check admin auth session
      const { fetchAuthSession, getCurrentUser } = await import('aws-amplify/auth');
      try {
        const [session, user] = await Promise.all([fetchAuthSession(), getCurrentUser()]);
        console.log('🔧 DEBUG: Admin auth session', {
          hasCredentials: !!session.credentials,
          identityId: session.identityId,
          hasTokens: !!session.tokens,
          userSub: session.tokens?.idToken?.payload?.sub,
          username: user?.username,
          groups: session.tokens?.accessToken?.payload?.['cognito:groups']
        });
      } catch (authError) {
        console.error('🔧 DEBUG: Admin auth check failed:', authError);
      }
      
      const client = generateClient<Schema>({
        authMode: 'userPool'
      });
      
      console.log('🔧 DEBUG: Admin client generated with authMode: userPool');

      // Fetch all courses with partner and sessions data
      console.log('🔍 Fetching courses for admin overview...');
      
              // First, let's try basic course data WITHOUT relationships to debug
        console.log('🔍 Step 1: Fetching course data (simplified query)...');
        const basicResult = await client.models.Course.list({
          selectionSet: [
            'id', 'courseId', 'title', 'description', 'price', 
            'location', 'startDate', 'endDate', 'targetgroup', 
            'partnerId', 'tutorId', 'isActive',
            'createdAt', 'updatedAt', 'urlSlug'
          ]
        });
      let data = basicResult.data;
      const errors = basicResult.errors;
      
      console.log('🔧 DEBUG: Admin Course list result', {
        success: !!data,
        courseCount: data?.length || 0,
        hasErrors: !!(errors && errors.length > 0),
        errors: errors?.map((e: any) => ({
          message: e.message,
          errorType: e.errorType || 'unknown',
          path: e.path
        }))
      });
      
      console.log('✅ Basic courses loaded:', data?.length || 0);
      console.log('🔍 First course urlSlug check:', (data?.[0] as any)?.urlSlug || 'NOT FOUND');
      
      // Now let's manually fetch partner data for each course
      if (data && data.length > 0) {
        console.log('🔍 Step 2: Fetching partner data for each course...');
        const enrichedData = await Promise.all(
          data.map(async (course: any) => {
            if (course.partnerId) {
              try {
                const partnerResult = await client.models.Partner.get({ id: course.partnerId });
                return {
                  ...course,
                  partner: partnerResult.data
                };
              } catch (partnerError) {
                console.warn(`⚠️ Failed to fetch partner for course ${course.title}:`, partnerError);
                return course;
              }
            }
            return course;
          })
        );
        data = enrichedData;
        console.log('✅ Partner data enriched for courses');
        
        // Filter out inactive sessions from each course
        data = data.map((course: any) => {
          if (course.sessions && Array.isArray(course.sessions)) {
            const totalSessions = course.sessions.length;
            course.sessions = course.sessions.filter((session: any) => session.isActive !== false);
            const activeSessions = course.sessions.length;
            if (totalSessions !== activeSessions) {
              console.log(`🗑️ Course "${course.title}": Filtered ${totalSessions - activeSessions} inactive sessions (${activeSessions} active)`);
            }
          }
          return course;
        });
        
        console.log('✅ Sessions data included and filtered');
        
        // Debug the enriched data
        data?.forEach((course: any, index: number) => {
          console.log(`🔍 Enriched Course ${index + 1}:`, {
            title: course.title,
            urlSlug: course.urlSlug,
            partnerId: course.partnerId,
            partnerName: course.partner?.name,
            hasPartner: !!course.partner,
            sessionsCount: course.sessions?.length || 0
          });
        });
      }

      if (errors && errors.length > 0) {
        console.error('🔴 Course query errors:', JSON.stringify(errors, null, 2));
        setError('Failed to load courses');
      } else {
        setCourses((data || []) as unknown as Course[]);
        setError(null);
        
        // Fetch participant counts after courses are loaded
        await fetchParticipantCounts();
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
      setError('Failed to load courses');
    } finally {
      setLoading(false);
    }
  };

  const fetchParticipantCounts = async () => {
    try {
      const client = generateClient<Schema>({ authMode: 'userPool' });
      
      // Fetch ALL registrations with pagination to handle large datasets
      // This matches the fix in AdminInvoices.tsx
      let allRegistrations: any[] = [];
      let nextToken: string | null | undefined = null;
      
      do {
        const response: any = await client.models.Registration.list({
          limit: 1000,
          nextToken: nextToken as any
        });
        
        allRegistrations = [...allRegistrations, ...(response.data || [])];
        nextToken = response.nextToken;
        
        console.log(`📊 AdminCoursesOverview: Fetched ${response.data?.length || 0} registrations (total: ${allRegistrations.length})`);
      } while (nextToken);
      
      console.log(`✅ AdminCoursesOverview: Total registrations fetched: ${allRegistrations.length}`);
      
      if (allRegistrations) {
        const counts: Record<string, { open: number; invoiced: number; paid: number }> = {};
        
        // Helper function to map registration status to invoice status
        // This matches the logic in AdminInvoices.tsx
        const determineInvoiceStatus = (registrationStatus: string): 'open' | 'invoiced' | 'paid' => {
          switch (registrationStatus) {
            case 'confirmed': return 'paid';
            case 'invoiced': return 'invoiced';
            case 'approved': return 'open';
            default: return 'open'; // pending and others default to 'open'
          }
        };
        
        // Filter out deleted registrations and group by courseId
        allRegistrations.forEach((registration: any) => {
          // Skip deleted registrations (matches AdminInvoices logic)
          if (registration.status === 'deleted') {
            return;
          }
          
          const courseId = registration.courseId;
          const invoiceStatus = determineInvoiceStatus(registration.status);
          
          if (!counts[courseId]) {
            counts[courseId] = { open: 0, invoiced: 0, paid: 0 };
          }
          
          counts[courseId][invoiceStatus]++;
        });
        
        console.log(`📊 Participant counts calculated for ${Object.keys(counts).length} courses`);
        setParticipantCounts(counts);
      }
    } catch (err) {
      console.error('Error fetching participant counts:', err);
    }
  };

  const handleToggleActive = async (course: Course) => {
    try {
      const client = generateClient<Schema>({ authMode: 'userPool' });
      await client.models.Course.update({
        id: course.id,
        isActive: !course.isActive
      });
      await fetchCourses();
    } catch (err) {
      console.error('Error updating course:', err);
      setMessage({ type: 'error', text: 'Failed to update course status' });
    }
  };

  const handleVisitCourse = (course: Course) => {
    if (course.urlSlug) {
      // Navigate to the public course page in the same tab
      window.location.href = `/courses/${course.urlSlug}`;
    } else {
      // If no URL slug, show a message
      setMessage({ 
        type: 'warning', 
        text: `Course "${course.title}" doesn't have a URL slug set yet. Please edit the course to add one.` 
      });
    }
  };

  const handleDeleteCourse = (courseId: string) => {
    const course = courses.find(c => c.id === courseId);
    if (course) {
      setConfirmDialog({
        isOpen: true,
        courseId,
        courseTitle: course.title
      });
    }
  };

  const confirmDeleteCourse = async () => {
    const { courseId } = confirmDialog;
    setConfirmDialog({ isOpen: false, courseId: '', courseTitle: '' });

    try {
      const client = generateClient<Schema>({ authMode: 'userPool' });
      
      // SOFT DELETE: Set isActive to false (sandbox schema doesn't have status/deletedAt yet)
      // This preserves data immutability - never hard delete user data
      await client.models.Course.update({ 
        id: courseId,
        isActive: false
      });
      
      await fetchCourses();
      setMessage({ type: 'success', text: 'Course moved to deleted items!' });
    } catch (err) {
      console.error('Error deleting course:', err);
      setMessage({ type: 'error', text: 'Failed to delete course' });
    }
  };

  const handleRestoreCourse = async (courseId: string) => {
    try {
      const client = generateClient<Schema>({ authMode: 'userPool' });
      
      // Restore course: set isActive back to true
      await client.models.Course.update({ 
        id: courseId,
        isActive: true
      });
      
      await fetchCourses();
      setMessage({ type: 'success', text: 'Course restored successfully!' });
    } catch (err) {
      console.error('Error restoring course:', err);
      setMessage({ type: 'error', text: 'Failed to restore course' });
    }
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Separate active and deactivated courses
  // Filter logic: deactivated have isActive=false (sandbox schema doesn't have status field yet)
  const deletedCourses: Course[] = []; // No soft delete in sandbox yet
  const activeCourses = courses.filter(course => course.isActive);
  const deactivatedCourses = courses.filter(course => !course.isActive);

  // Sort active courses
  const sortedActiveCourses = [...activeCourses].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    // Handle price sorting (remove € and convert to number)
    if (sortField === 'price') {
      aValue = parseFloat(aValue.replace(/[€,]/g, '')) || 0;
      bValue = parseFloat(bValue.replace(/[€,]/g, '')) || 0;
    }

    // Handle date sorting
    if (sortField === 'startDate' || sortField === 'endDate') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }

    // Handle boolean sorting (isActive)
    if (sortField === 'isActive') {
      aValue = aValue ? 1 : 0;
      bValue = bValue ? 1 : 0;
    }

    // Handle string sorting
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Sort deactivated courses
  const sortedDeactivatedCourses = [...deactivatedCourses].sort((a, b) => {
    let aValue: any = a[sortField];
    let bValue: any = b[sortField];

    // Handle price sorting (remove € and convert to number)
    if (sortField === 'price') {
      aValue = parseFloat(aValue.replace(/[€,]/g, '')) || 0;
      bValue = parseFloat(bValue.replace(/[€,]/g, '')) || 0;
    }

    // Handle date sorting
    if (sortField === 'startDate' || sortField === 'endDate') {
      aValue = aValue ? new Date(aValue).getTime() : 0;
      bValue = bValue ? new Date(bValue).getTime() : 0;
    }

    // Handle boolean sorting (isActive)
    if (sortField === 'isActive') {
      aValue = aValue ? 1 : 0;
      bValue = bValue ? 1 : 0;
    }

    // Handle string sorting
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }

    if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const renderTable = () => (
    <div className="admin-content-card" style={{ padding: 0, marginBottom: '24px' }}>
      {sortedActiveCourses.length === 0 ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: '#6b7280' }}>
          <p>No active courses found.</p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto', margin: 0, padding: 0 }}>
          <table className="admin-table" style={{ margin: 0 }}>
            <thead>
              <tr>
                <th onClick={() => handleSort('courseId')} style={{ cursor: 'pointer' }}>
                  ID {sortField === 'courseId' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                  Course Title {sortField === 'title' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('urlSlug')} style={{ cursor: 'pointer' }}>
                  URL Slug {sortField === 'urlSlug' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th onClick={() => handleSort('location')} style={{ cursor: 'pointer' }}>
                  Location {sortField === 'location' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Partner</th>
                <th>Sessions</th>
                <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>
                  Price {sortField === 'price' && (sortDirection === 'asc' ? '↑' : '↓')}
                </th>
                <th>Participants</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {sortedActiveCourses.map((course) => (
                <tr key={course.id}>
                  <td>
                    <div style={{ fontWeight: 600, color: '#008F99' }}>
                      {course.courseId || '-'}
                    </div>
                  </td>
                  <td>
                    <div style={{ color: '#1f2937' }}>
                      {course.title}
                    </div>
                  </td>
                  <td>{(course as any).urlSlug || '-'}</td>
                  <td>{course.location || '-'}</td>
                  <td>
                    {course.partner?.name || (course.partnerId ? (
                      <span style={{ color: '#ef4444', fontSize: '0.9rem' }}>
                        Partner not found (ID: {course.partnerId.substring(0, 8)}...)
                      </span>
                    ) : '-')}
                  </td>
                  <td>
                    {course.sessions && course.sessions.length > 0 ? (
                      <div style={{ fontSize: '0.9rem' }}>
                        <div style={{ fontWeight: 600, color: '#008F99' }}>
                          {course.sessions.length} Session{course.sessions.length > 1 ? 's' : ''}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                          {course.sessions.filter(s => s.locationType === 'ONLINE').length > 0 && 
                           course.sessions.filter(s => s.locationType === 'ON_SITE').length > 0 ? 
                            'Mixed (Online & On-site)' :
                            course.sessions[0]?.locationType === 'ONLINE' ? 'Online Only' : 'On-site Only'
                          }
                        </div>
                      </div>
                    ) : (
                      <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No sessions</span>
                    )}
                  </td>
                  <td>{course.price}</td>
                  <td>
                    <div style={{ fontWeight: 500, color: '#374151' }}>
                      {(() => {
                        const counts = participantCounts[course.id] || { open: 0, invoiced: 0, paid: 0 };
                        return `${counts.open}/${counts.invoiced}/${counts.paid}`;
                      })()}
                    </div>
                  </td>
                  <td>
                    <div className="actions">
                      {onEditCourse && (
                        <button
                          onClick={() => onEditCourse(course.id)}
                          style={{
                            padding: '6px 12px',
                            border: '1px solid #8B5A2B',
                            borderRadius: '6px',
                            background: 'white',
                            color: '#8B5A2B',
                            fontSize: '12px',
                            cursor: 'pointer',
                            marginRight: '8px'
                          }}
                        >
                          Edit
                        </button>
                      )}
                      <button
                        onClick={() => handleToggleActive(course)}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #008F99',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#008F99',
                          fontSize: '12px',
                          cursor: 'pointer',
                          marginRight: '8px'
                        }}
                      >
                        {course.isActive ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleVisitCourse(course)}
                        style={{
                          padding: '6px 12px',
                          border: '1px solid #008F99',
                          borderRadius: '6px',
                          background: 'white',
                          color: '#008F99',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Visit
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Deactivated Courses Table */}
      {deactivatedCourses.length > 0 && (
        <div style={{ marginTop: '32px' }}>
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center', 
            marginBottom: '16px',
            padding: '0 24px'
          }}>
            <h3 style={{ 
              margin: 0, 
              color: '#1f2937', 
              fontSize: '18px', 
              fontWeight: 600 
            }}>
              Deactivated Courses ({deactivatedCourses.length})
            </h3>
            <button
              onClick={() => setShowDeactivatedTable(!showDeactivatedTable)}
              style={{
                padding: '8px 16px',
                border: '1px solid #008F99',
                borderRadius: '6px',
                background: 'white',
                color: '#008F99',
                fontSize: '14px',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              {showDeactivatedTable ? 'Hide' : 'Show'} Deactivated
            </button>
          </div>

          {showDeactivatedTable && (
            <div className="admin-content-card" style={{ padding: 0 }}>
              <div style={{ overflowX: 'auto', margin: 0, padding: 0 }}>
                <table className="admin-table" style={{ margin: 0 }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('courseId')} style={{ cursor: 'pointer' }}>
                        ID {sortField === 'courseId' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                        Course Title {sortField === 'title' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('urlSlug')} style={{ cursor: 'pointer' }}>
                        URL Slug {sortField === 'urlSlug' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th onClick={() => handleSort('location')} style={{ cursor: 'pointer' }}>
                        Location {sortField === 'location' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>Partner</th>
                      <th>Sessions</th>
                      <th onClick={() => handleSort('price')} style={{ cursor: 'pointer' }}>
                        Price {sortField === 'price' && (sortDirection === 'asc' ? '↑' : '↓')}
                      </th>
                      <th>Participants</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedDeactivatedCourses.map((course) => (
                      <tr key={course.id}>
                        <td>
                          <div style={{ fontWeight: 600, color: '#008F99' }}>
                            {course.courseId || '-'}
                          </div>
                        </td>
                        <td>
                          <div style={{ color: '#1f2937' }}>
                            {course.title}
                          </div>
                        </td>
                        <td>{(course as any).urlSlug || '-'}</td>
                        <td>{course.location || '-'}</td>
                        <td>
                          {course.partner?.name || (course.partnerId ? (
                            <span style={{ color: '#ef4444', fontSize: '0.9rem' }}>
                              Partner not found (ID: {course.partnerId.substring(0, 8)}...)
                            </span>
                          ) : '-')}
                        </td>
                        <td>
                          {course.sessions && course.sessions.length > 0 ? (
                            <div style={{ fontSize: '0.9rem' }}>
                              <div style={{ fontWeight: 600, color: '#008F99' }}>
                                {course.sessions.length} Session{course.sessions.length > 1 ? 's' : ''}
                              </div>
                              <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                                {course.sessions.filter(s => s.locationType === 'ONLINE').length > 0 && 
                                 course.sessions.filter(s => s.locationType === 'ON_SITE').length > 0 ? 
                                  'Mixed (Online & On-site)' :
                                  course.sessions[0]?.locationType === 'ONLINE' ? 'Online Only' : 'On-site Only'
                                }
                              </div>
                            </div>
                          ) : (
                            <span style={{ color: '#9ca3af', fontStyle: 'italic' }}>No sessions</span>
                          )}
                        </td>
                        <td>{course.price}</td>
                        <td>
                          <div style={{ fontWeight: 500, color: '#374151' }}>
                            {(() => {
                              const counts = participantCounts[course.id] || { open: 0, invoiced: 0, paid: 0 };
                              return `${counts.open}/${counts.invoiced}/${counts.paid}`;
                            })()}
                          </div>
                        </td>
                        <td>
                          <div className="actions">
                            <button
                              onClick={() => handleToggleActive(course)}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #008F99',
                                borderRadius: '6px',
                                background: 'white',
                                color: '#008F99',
                                fontSize: '12px',
                                cursor: 'pointer'
                              }}
                            >
                              Activate
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Loading courses...</h2>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content-card">
        <div className="admin-toolbar">
          <h2>Error: {error}</h2>
          <button className="admin-btn primary" onClick={fetchCourses}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ paddingBottom: '60px', minHeight: 'calc(100vh - 77px)', display: 'flex', flexDirection: 'column' }}>
      {message && (
        <div style={{ marginBottom: '16px' }}>
          <StatusMessage
            type={message.type}
            message={message.text}
            onClose={() => setMessage(null)}
          />
        </div>
      )}
      
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title="Delete Course"
        message={`Are you sure you want to delete the course "${confirmDialog.courseTitle}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        onConfirm={confirmDeleteCourse}
        onCancel={() => setConfirmDialog({ isOpen: false, courseId: '', courseTitle: '' })}
      />
      
      <div style={{ flex: 1 }}>
        {renderTable()}
      </div>
      
      {/* Blank footer */}
      <div style={{
        position: 'fixed',
        bottom: 0,
        left: '280px',
        right: 0,
        background: 'white',
        borderTop: '1px solid #e5e7eb',
        boxShadow: '0 -4px 10px rgba(0, 0, 0, 0.06)',
        zIndex: 200,
        padding: '12px 24px',
        height: '48px'
      }} />
    </div>
  );
};

export default AdminCoursesOverview;
